import React, { useState, useEffect } from 'react';
import { Box, Stack, Typography, Button, Popover, MenuItem, Select, FormControl } from '@mui/material';
import { styled } from '@mui/material/styles';
import DropdownIcon from '../icons/DropdownIcon';

const PopoverContent = styled(Box)(({ theme }) => ({
  width: '280px',
  padding: '14px',
  border: '0.6px solid #0b91d6',
  borderRadius: '12px',
  backgroundColor: '#ffffff',
  [theme.breakpoints.up('sm')]: {
    width: '300px',
    padding: '16px'
  }
}));

const SortTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '15px',
  fontWeight: 500,
  color: '#121927',
  marginBottom: '12px',
  [theme.breakpoints.up('sm')]: {
    fontSize: '16px',
    marginBottom: '16px'
  }
}));

const Divider = styled(Box)(({ theme }) => ({
  height: '1px',
  backgroundColor: '#a0a0aa',
  border: '0.6px solid #a0a0aa',
  margin: '12px 0',
  [theme.breakpoints.up('sm')]: {
    margin: '16px 0'
  }
}));

const SortSection = styled(Stack)(({ theme }) => ({
  gap: '12px',
  [theme.breakpoints.up('sm')]: {
    gap: '17px'
  }
}));

const SortLabel = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 500,
  color: '#121927',
  [theme.breakpoints.up('sm')]: {
    fontSize: '16px'
  }
}));

const SortSelect = styled(FormControl)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    border: '0.2px solid #a0a0aa',
    backgroundColor: '#ffffff',
    minHeight: '44px',
    '& fieldset': {
      border: 'none'
    }
  },
  '& .MuiSelect-select': {
    fontFamily: 'Inter, sans-serif',
    fontSize: '13px',
    fontWeight: 500,
    color: '#0b91d6',
    padding: '12px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    [theme.breakpoints.up('sm')]: {
      fontSize: '14px',
      padding: '16px'
    }
  }
}));

const ActionButtons = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'space-between',
  marginTop: '12px',
  gap: '12px',
  [theme.breakpoints.up('sm')]: {
    marginTop: '16px'
  }
}));

const ResetButton = styled(Button)(({ theme }) => ({
  color: '#ef232a',
  fontFamily: 'Inter, sans-serif',
  fontSize: '13px',
  fontWeight: 600,
  textTransform: 'capitalize',
  backgroundColor: 'transparent',
  border: 'none',
  padding: '8px 12px',
  minHeight: '40px',
  WebkitTapHighlightColor: 'transparent',
  '&:active': {
    transform: 'scale(0.97)',
    backgroundColor: 'rgba(239, 35, 42, 0.08)'
  },
  [theme.breakpoints.up('sm')]: {
    fontSize: '14px',
    padding: '8px 16px'
  },
  [theme.breakpoints.up('md')]: {
    '&:hover': {
      backgroundColor: 'rgba(239, 35, 42, 0.04)'
    },
    '&:active': {
      transform: 'none'
    }
  }
}));

const ApplyButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#0b91d6',
  color: '#ffffff',
  fontFamily: 'Inter, sans-serif',
  fontSize: '13px',
  fontWeight: 600,
  textTransform: 'capitalize',
  borderRadius: '6px',
  padding: '8px 18px',
  minHeight: '40px',
  WebkitTapHighlightColor: 'transparent',
  '&:active': {
    transform: 'scale(0.97)',
    backgroundColor: '#0875a8'
  },
  [theme.breakpoints.up('sm')]: {
    fontSize: '14px',
    borderRadius: '4px',
    padding: '8px 24px'
  },
  [theme.breakpoints.up('md')]: {
    '&:hover': {
      backgroundColor: '#0980c2'
    },
    '&:active': {
      transform: 'none'
    }
  }
}));

const SortPopup = ({ open, anchorEl, onClose, sortBy, onApply }) => {
  const [localSort, setLocalSort] = useState(sortBy);

  // Keep local state in sync with parent when opening or when sortBy changes
  useEffect(() => {
    if (open) {
      setLocalSort(sortBy);
    }
  }, [open, sortBy]);

  const handleSortChange = (value) => {
    setLocalSort(value);
  };

  const handleReset = () => {
    setLocalSort('new');
  };

  const handleApply = () => {
    onApply(localSort);
  };

  const getSortLabel = (value) => {
    switch (value) {
      case 'new': return 'Newest First';
      case 'old': return 'Oldest First';
      case 'high-rating': return 'High Rating';
      case 'low-rating': return 'Low Rating';
      default: return 'Newest First';
    }
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      PaperProps={{
        sx: { boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)' }
      }}
    >
      <PopoverContent>
        <SortTitle>Sort By</SortTitle>
        <Divider />
        
        <SortSection>
          <SortLabel>Sort order</SortLabel>
          <SortSelect fullWidth>
            <Select
              value={localSort}
              onChange={(e) => handleSortChange(e.target.value)}
              IconComponent={() => <DropdownIcon width={7} height={4} color="#a0a0aa" />}
            >
              <MenuItem value="new">Newest First</MenuItem>
              <MenuItem value="old">Oldest First</MenuItem>
              <MenuItem value="high-rating">High Rating</MenuItem>
              <MenuItem value="low-rating">Low Rating</MenuItem>
            </Select>
          </SortSelect>
        </SortSection>
        
        <Divider />
        
        <ActionButtons>
          <ResetButton onClick={handleReset}>
            Reset
          </ResetButton>
          <ApplyButton onClick={handleApply}>
            Apply
          </ApplyButton>
        </ActionButtons>
      </PopoverContent>
    </Popover>
  );
};

export default SortPopup;